import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Trash2,
  Plus,
  Minus,
  ShoppingCart,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ShieldCheck,
  Truck,
  Sparkles,
  CreditCard,
  Loader,
  MapPin,
  Edit3,
  X,
  CheckCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth, api } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useAuthModal } from "../context/AuthModalContext";
import { useBike } from "../context/BikeContext";
import LocationAutofillButton from "../components/LocationAutofillButton";

// Indian States and Union Territories
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry"
];

const emptyAddressForm = {
  house_flat_no: "", // Required: House / Flat Number
  street_area: "", // Required: Street / Area
  building_name: "", // Optional: Building Name
  apartment_suite: "", // Optional: Apartment / Suite
  floor: "", // Optional: Floor
  landmark: "", // Optional: Landmark
  city: "", // Required: City
  state_province: "", // Required: State / Province
  postal_code: "", // Required: Postal Code / ZIP
  country: "India", // Default to India for domestic
  district_county: "", // Optional: District / County
  latitude: null,
  longitude: null,
};

const formatAddress = (address) => {
  if (!address) return "";

  return [
    address.building_name,
    address.house_flat_no || address.flat_no || address.building_no,
    address.apartment_suite ? `Apt ${address.apartment_suite}` : address.apt_no ? `Apt ${address.apt_no}` : null,
    address.floor ? `Floor ${address.floor}` : null,
    address.street_area || address.address_line1 || address.street,
    address.landmark || address.address_line2,
    address.district_county || address.district || null,
    [address.city, address.state_province || address.state].filter(Boolean).join(", "),
    address.country || null,
    address.postal_code || address.pincode,
  ]
    .filter(Boolean)
    .join(", ");
};

const cartLineKey = (item) => {
  if (item.item_type === "combo") {
    return [
      "combo",
      item.combo_id || item.product_id,
      JSON.stringify(item.selected_children || []),
    ].join("|");
  }

  return [
    "product",
    item.product_id,
    item.size || "",
    item.color || "",
  ].join("|");
};

const getApiMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail || error?.response?.data?.message;
  if (typeof detail === "string") return detail;
  if (detail) return JSON.stringify(detail);
  return fallback;
};

export default function Cart() {
  const navigate = useNavigate();
  const { isAuthenticated, user, refreshUser } = useAuth();
  const { refreshCartCount } = useCart();
  const { openAuth } = useAuthModal();
  const { selectedBike } = useBike();

  const [cart, setCart] = useState({ items: [] });
  const [recommendations, setRecommendations] = useState([]);
  const [userData, setUserData] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState(null);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [editingAddressId, setEditingAddressId] = useState(null);
  const [addressForm, setAddressForm] = useState(emptyAddressForm);
  const [showAddressEditModal, setShowAddressEditModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [updating, setUpdating] = useState(null);
  const [mobileSummaryOpen, setMobileSummaryOpen] = useState(false);
  const [shippingInfo, setShippingInfo] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [shippingError, setShippingError] = useState(null);
  const shippingRequestId = useRef(0);
  const [isInternational, setIsInternational] = useState(false); // Default to Domestic

  const shippingRecalcKey = useMemo(
    () => JSON.stringify({
      postal_code: selectedAddress?.postal_code || selectedAddress?.pincode || "",
      country: selectedAddress?.country || "",
      items: (cart.items || []).map((item) => ({
        key: cartLineKey(item),
        quantity: Number(item.quantity || 0),
        price: Number(item.price || 0),
      })),
    }),
    [selectedAddress?.postal_code, selectedAddress?.pincode, selectedAddress?.country, cart.items]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/"); // Redirect to home without showing popup
      return;
    }
    fetchCart();
    fetchRecommendations();
    fetchUser();
    fetchAddresses();
  }, [isAuthenticated]);

  useEffect(() => {
    checkShipping(cart, selectedAddress);
  }, [shippingRecalcKey]);

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const fetchUser = async () => {
    try {
      const res = await api.get("/auth/me");
      setUserData(res.data);
    } catch {
      setUserData(user || null);
    }
  };

  const fetchAddresses = async () => {
    try {
      const res = await api.get("/user/addresses");
      const list = res.data.addresses || [];
      setAddresses(list);
      setSelectedAddress((prev) => {
        if (prev && list.some((item) => item.id === prev.id)) {
          return list.find((item) => item.id === prev.id);
        }
        return list.find((item) => item.is_default) || list[0] || null;
      });
    } catch {
      setAddresses([]);
    }
  };

  const saveAddress = async () => {
    // Required fields validation
    if (!addressForm.house_flat_no?.trim() || !addressForm.street_area?.trim() || 
        !addressForm.city?.trim() || !addressForm.state_province?.trim() || 
        !addressForm.postal_code?.trim() || !addressForm.country?.trim()) {
      toast.error("Please fill all required fields");
      return;
    }

    // Domestic (India) validation for postal code (6 digits)
    if (!isInternational && !/^[0-9]{6}$/.test(addressForm.postal_code)) {
      toast.error("Enter a valid 6-digit pincode for India");
      return;
    }

    // International validation for postal code (minimum 3 characters)
    if (isInternational && addressForm.postal_code.length < 3) {
      toast.error("Enter a valid postal code (minimum 3 characters)");
      return;
    }

    // For domestic, ensure country is India
    if (!isInternational && addressForm.country?.toLowerCase() !== 'india') {
      toast.error("Country must be India for domestic shipping");
      return;
    }

    try {
      const res = editingAddressId
        ? await api.put(`/user/addresses/${editingAddressId}`, addressForm)
        : await api.post("/user/addresses", addressForm);
      const list = res.data.addresses || [];
      setAddresses(list);
      const saved = list.find((item) => item.id === editingAddressId) || list[list.length - 1] || null;
      setSelectedAddress(saved);
      setAddressForm(emptyAddressForm);
      setEditingAddressId(null);
      setShowAddressForm(false);
      setShowAddressEditModal(false); // Close edit modal
      setAddressModalOpen(false);
      setIsInternational(false); // Reset to domestic after save
      toast.success("Address saved");
      // Recalculate shipping after address save
      await checkShipping();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Address save failed");
    }
  };

  const openAddressModal = ({ adding = false } = {}) => {
    // Close mobile summary if open
    setMobileSummaryOpen(false);
    setAddressModalOpen(true);
    setEditingAddressId(null);
    setAddressForm(emptyAddressForm);
    setShowAddressForm(adding || addresses.length === 0);
  };

  const startEditAddress = (address) => {
    setEditingAddressId(address.id);
    const country = address.country || "";
    
    // Set international status based on country
    if (country && country.toLowerCase() !== 'india') {
      setIsInternational(true);
    } else {
      setIsInternational(false);
    }

    setAddressForm({
      // New field names with fallback to old field names for backward compatibility
      house_flat_no: address.house_flat_no || address.flat_no || address.building_no || "",
      street_area: address.street_area || address.address_line1 || address.street || "",
      building_name: address.building_name || "",
      apartment_suite: address.apartment_suite || address.apt_no || "",
      floor: address.floor || "",
      landmark: address.landmark || address.address_line2 || "",
      city: address.city || "",
      state_province: address.state_province || address.state || "",
      postal_code: address.postal_code || address.pincode || "",
      country: country,
      district_county: address.district_county || address.district || "",
      latitude: address.latitude ?? null,
      longitude: address.longitude ?? null,
    });
    setShowAddressForm(true);
    setShowAddressEditModal(true); // Open edit modal
  };

  const openAddAddressModal = () => {
    setEditingAddressId(null);
    setAddressForm({ ...emptyAddressForm });
    setIsInternational(false);
    setShowAddressForm(true);
    setShowAddressEditModal(true);
    setAddressModalOpen(false); // Close address selection modal
  };

  const closeAddressEditModal = () => {
    setShowAddressEditModal(false);
    setShowAddressForm(false);
    setEditingAddressId(null);
    setAddressForm({ ...emptyAddressForm });
    setIsInternational(false);
  };

  const applyLocationAutofill = (fields) => {
    setAddressForm((prev) => {
      const detectedCountry = fields.country || "";
      
      // Always fill all available fields from location detection (as requested)
      const updatedForm = {
        ...prev,
        // Map OSM fields to form fields
        house_flat_no: fields.house_flat_no || prev.house_flat_no || "",
        street_area: fields.street_area || prev.street_area || "",
        country: detectedCountry || (isInternational ? "" : "India"),
        state_province: fields.state_province || "",
        district_county: fields.district_county || "",
        city: fields.city || "",
        postal_code: fields.postal_code || "",
        latitude: fields.latitude ?? null,
        longitude: fields.longitude ?? null,
      };

      // Auto-detect if international based on detected country
      if (detectedCountry && detectedCountry.toLowerCase() !== 'india') {
        setIsInternational(true);
      } else if (detectedCountry && detectedCountry.toLowerCase() === 'india') {
        setIsInternational(false);
      }

      return updatedForm;
    });
  };

  const selectAddress = (address) => {
    setSelectedAddress(address);
    setAddressModalOpen(false);
    setShowAddressForm(false);
    setEditingAddressId(null);
    setMobileSummaryOpen(false); // Close mobile summary after selecting address
    // Recalculate shipping when address changes
    checkShipping();
  };

  const deleteAddress = async (addressId) => {
    if (!window.confirm("Delete this address?")) return;

    try {
      const res = await api.delete(`/user/addresses/${addressId}`);
      const list = res.data.addresses || [];
      setAddresses(list);
      const previousSelectedId = selectedAddress?.id;
      setSelectedAddress((prev) => {
        if (prev?.id !== addressId) return prev;
        return list.find((item) => item.is_default) || list[0] || null;
      });
      // Recalculate shipping if the selected address changed
      if (previousSelectedId === addressId) {
        checkShipping();
      }
      if (list.length === 0) {
        setShowAddressForm(true);
      }
      toast.success("Address deleted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Address delete failed");
    }
  };

  const fetchCart = async ({ silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const res = await api.get("/cart");
      const nextCart = res.data || { items: [] };
      setCart(nextCart);
      return nextCart;
    } catch {
      toast.error("Failed to load cart");
      setCart({ items: [] });
      return { items: [] };
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchRecommendations = async () => {
    try {
      const res = await api.get("/cart/recommendations?limit=8");
      const allRecommendations = res.data || [];
      
      // Filter to only show simple products (no variants)
      const simpleProducts = allRecommendations.filter(product => {
        const hasColors = product.colors && product.colors.length > 0;
        const hasSizes = product.sizes && product.sizes.length > 0;
        const hasVariantStock = product.variant_stock && product.variant_stock.length > 0;
        const isCombo = product.product_type === 'combo' || product.item_type === 'combo';
        
        // Only include products without variants and not combos
        return !hasColors && !hasSizes && !hasVariantStock && !isCombo;
      });
      
      setRecommendations(simpleProducts);
    } catch {
      setRecommendations([]);
    }
  };

  const checkShipping = async (cartOverride = cart, addressOverride = selectedAddress) => {
    // Get postal code with fallback to legacy pincode field
    const postalCode = addressOverride?.postal_code || addressOverride?.pincode;
    const country = addressOverride?.country || "";

    if (!addressOverride || !postalCode || !cartOverride?.items?.length) {
      shippingRequestId.current += 1;
      setShippingInfo(null);
      setShippingError(null);
      setShippingLoading(false);
      return null;
    }

    const requestId = shippingRequestId.current + 1;
    shippingRequestId.current = requestId;

    try {
      setShippingLoading(true);
      setShippingError(null);
      const res = await api.post("/shipping/check", {
        delivery_pincode: postalCode,
        country: country
      });
      if (requestId !== shippingRequestId.current) return res.data;

      // Debug log: Frontend JSON received from backend
      console.log("=== FRONTEND: Received shipping response ===");
      console.log("Response data:", res.data);
      console.log("Serviceable:", res.data?.serviceable);
      console.log("Shipping charge:", res.data?.shipping_charge);
      console.log("Courier name:", res.data?.courier_name);
      console.log("Estimated delivery days:", res.data?.estimated_delivery_days);
      console.log("=====================================");

      setShippingInfo(res.data);
      if (!res.data?.serviceable) {
        setShippingError(res.data?.message || "Unable to calculate shipping. Please try again.");
      }
      return res.data;
    } catch (err) {
      console.error("Shipping check failed:", err);
      if (requestId === shippingRequestId.current) {
        setShippingError("Unable to calculate shipping. Please try again.");
        setShippingInfo(null);
      }
      return null;
    } finally {
      if (requestId === shippingRequestId.current) {
        setShippingLoading(false);
      }
    }
  };

  const updateQuantity = async (productId, qty, size, color) => {
    if (qty < 1) return;
    const key = productId + (size || "") + (color || "");

    setCart((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.product_id === productId && (item.size || null) === (size || null) && (item.color || null) === (color || null)
          ? { ...item, quantity: qty }
          : item
      ),
    }));

    try {
      setUpdating(key);
      await api.post("/cart/update", {
        product_id: productId,
        quantity: qty,
        size: size || null,
        color: color || null,
      });
      await fetchCart({ silent: true });
      refreshCartCount();
      // Recalculate shipping after quantity change
      await checkShipping();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
      fetchCart({ silent: true });
    } finally {
      setUpdating(null);
    }
  };

  const removeItem = async (productId, size, color) => {
    try {
      setUpdating(productId + (size || "") + (color || ""));
      await api.delete("/cart/remove", {
        params: { product_id: productId, size: size || null, color: color || null },
      });
      toast.success("Removed");
      await fetchCart({ silent: true });
      await fetchRecommendations();
      refreshCartCount();
      // Recalculate shipping after item removal
      await checkShipping();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally {
      setUpdating(null);
    }
  };

  const addRecommendation = async (product) => {
    try {
      await api.post("/cart/add", { product_id: product.id, quantity: 1 });
      toast.success(`${product.name} added to cart!`);
      await fetchCart({ silent: true });
      await fetchRecommendations();
      refreshCartCount();
      // Recalculate shipping after adding item
      await checkShipping();
    } catch (err) {
      const errorMessage = err?.response?.data?.detail || "Failed to add";
      toast.error(errorMessage);
    }
  };

  const subtotal = useMemo(
    () => cart.items.reduce(
      (sum, item) => sum + parseFloat(item.price || 0) * item.quantity,
      0
    ),
    [cart.items]
  );
  const shippingCharge = shippingInfo?.serviceable ? Number(shippingInfo.shipping_charge || 0) : 0;
  const discount = 0;
  const total = Math.max(subtotal + shippingCharge - discount, 0);
  const itemCount = useMemo(() => cart.items.reduce((sum, i) => sum + i.quantity, 0), [cart.items]);

  // Debug log: Final shipping state before rendering
  useEffect(() => {
    console.log("=== FRONTEND: Final shipping state before rendering ===");
    console.log("shippingInfo:", shippingInfo);
    console.log("shippingCharge:", shippingCharge);
    console.log("serviceable:", shippingInfo?.serviceable);
    console.log("======================================================");
  }, [shippingInfo, shippingCharge]);

  const activeUser = userData || user;
  const hasRealMobile = activeUser?.mobile && !activeUser.mobile.startsWith("g_");
  const checkoutDisabled = processing || shippingLoading || Boolean(selectedAddress && !shippingInfo?.serviceable);
  const codAvailability = shippingInfo?.cod_available
    ? "Available"
    : shippingInfo?.serviceable
      ? "Prepaid only"
      : "Check address";

  const money = (value) => `Rs. ${Number(value || 0).toLocaleString("en-IN")}`;

  const getProductImage = (item) => {
    // Handle both cart items and recommendation products
    const product = item.product || item;
    
    // Priority 1: color_images[selectedColor][0]
    if (item.color && item.color_images && item.color_images[item.color]?.[0]) {
      return item.color_images[item.color][0];
    }
    
    // Priority 2: product.color_images[selectedColor][0]
    if (item.color && product?.color_images && product.color_images[item.color]?.[0]) {
      return product.color_images[item.color][0];
    }
    
    // Case-insensitive matching for Priority 1
    if (item.color && item.color_images) {
      const normalizedColor = item.color.trim().toLowerCase();
      const matchingKey = Object.keys(item.color_images).find(
        key => key.trim().toLowerCase() === normalizedColor
      );
      if (matchingKey && item.color_images[matchingKey]?.[0]) {
        return item.color_images[matchingKey][0];
      }
    }
    
    // Case-insensitive matching for Priority 2
    if (item.color && product?.color_images) {
      const normalizedColor = item.color.trim().toLowerCase();
      const matchingKey = Object.keys(product.color_images).find(
        key => key.trim().toLowerCase() === normalizedColor
      );
      if (matchingKey && product.color_images[matchingKey]?.[0]) {
        return product.color_images[matchingKey][0];
      }
    }
    
    // Priority 3: thumbnail (check both item and product)
    if (typeof item.thumbnail === "string" && item.thumbnail) {
      return item.thumbnail;
    }
    if (typeof product?.thumbnail === "string" && product.thumbnail) {
      return product.thumbnail;
    }
    
    // Priority 4: image (check both item and product)
    if (typeof item.image === "string" && item.image) {
      return item.image;
    }
    if (typeof product?.image === "string" && product.image) {
      return product.image;
    }
    
    // Priority 5: images[0] (check both item and product)
    if (Array.isArray(item.images) && item.images.length > 0) {
      return item.images[0];
    }
    if (Array.isArray(product?.images) && product.images.length > 0) {
      return product.images[0];
    }
    
    // Priority 6: placeholder
    return "/placeholder.svg";
  };

  const validateCartBeforePayment = async () => {
    const displayedItems = cart.items || [];
    const res = await api.get("/cart");
    const latestCart = res.data || { items: [] };
    const latestItems = latestCart.items || [];

    setCart(latestCart);
    refreshCartCount();

    if (latestItems.length === 0) {
      throw new Error("This product is currently unavailable. Please remove it from your cart.");
    }

    const latestByKey = new Map(latestItems.map((item) => [cartLineKey(item), item]));

    for (const item of displayedItems) {
      const latestItem = latestByKey.get(cartLineKey(item));

      if (!latestItem) {
        throw new Error(`${item.name} is currently unavailable. Please remove it from your cart.`);
      }

      if (Number(latestItem.quantity || 0) < Number(item.quantity || 0)) {
        throw new Error(`Only ${latestItem.quantity} items left in stock for ${item.name}.`);
      }

      if (Number(latestItem.price || 0) !== Number(item.price || 0)) {
        throw new Error(`Price changed for ${item.name}. Please review your cart before payment.`);
      }

      if ((latestItem.size || "") !== (item.size || "") || (latestItem.color || "") !== (item.color || "")) {
        throw new Error(`Selected variant is unavailable for ${item.name}. Please update your cart.`);
      }
    }

    const latestSubtotal = latestItems.reduce(
      (sum, item) => sum + parseFloat(item.price || 0) * Number(item.quantity || 0),
      0
    );
    const latestShipping = await checkShipping(latestCart, selectedAddress);

    if (!latestShipping?.serviceable) {
      throw new Error(latestShipping?.message || "Unable to calculate shipping. Please try again.");
    }

    return {
      latestCart,
      latestShipping,
      latestSubtotal,
      latestTotal: latestSubtotal + Number(latestShipping.shipping_charge || 0),
    };
  };

  const handleDirectCheckout = async () => {
    if (!hasRealMobile) {
      toast.error("Mobile number is required before checkout");
      openAuth("account", true); // Keep mobile popup required for checkout
      return;
    }
    if (!selectedAddress) {
      toast.error("Choose or add delivery address");
      openAddressModal({ adding: addresses.length === 0 });
      return;
    }
    if (shippingLoading) {
      toast.error("Shipping charge is still being calculated");
      return;
    }
    if (!shippingInfo?.serviceable) {
      toast.error(shippingError || "Delivery is unavailable for this address");
      return;
    }

    try {
      setProcessing(true);
      const {
        latestCart,
        latestShipping,
        latestTotal,
      } = await validateCartBeforePayment();
      const latestItems = latestCart.items || [];
      const latestItemCount = latestItems.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      const ready = await loadRazorpayScript();
      if (!ready) {
        toast.error("Razorpay SDK failed to load");
        return;
      }

      const shippingAddress = {
        full_name: activeUser?.name || "Customer",
        mobile: activeUser?.mobile || activeUser?.phone || "",
        building_name: selectedAddress.building_name || "",
        building_no: selectedAddress.building_no || "",
        apt_no: selectedAddress.apt_no || "",
        floor: selectedAddress.floor || "",
        flat_no: selectedAddress.flat_no || "",
        address_line1: selectedAddress.address_line1 || selectedAddress.street || "",
        address_line2: selectedAddress.address_line2 || selectedAddress.landmark || "",
        city: selectedAddress.city,
        state: selectedAddress.state,
        pincode: selectedAddress.pincode,
        landmark: selectedAddress.landmark || "",
        country: selectedAddress.country || "",
        district: selectedAddress.district || "",
        latitude: selectedAddress.latitude ?? null,
        longitude: selectedAddress.longitude ?? null,
        display_name: selectedAddress.display_name || "",
      };
      const orderItems = latestItems.map((item) => ({
        ...item,
        bike: selectedBike ? {
          brand: selectedBike.brand,
          model: selectedBike.model,
          variant: selectedBike.variant,
        } : null,
        selected_children: item.item_type === "combo" ? (item.selected_children || []) : undefined,
      }));

      const orderData = {
        items: orderItems,
        total_amount: latestTotal,
        shipping_address: shippingAddress,
        payment_method: "razorpay",
        notes: selectedBike ? `Bike: ${selectedBike.brand} ${selectedBike.model} ${selectedBike.variant}` : "Bike not selected",
        shipping_charge: latestShipping?.shipping_charge,
        selected_courier_id: latestShipping?.courier_company_id,
        selected_courier_name: latestShipping?.courier_name,
        estimated_delivery_days: latestShipping?.estimated_delivery_days,
        delivery_speed: latestShipping?.delivery_speed,
        total_weight: latestShipping?.total_weight,
        box_dimensions: latestShipping?.box_dimensions,
      };

      const razorpayOrder = await api.post("/razorpay/create-order", {
        ...orderData,
        currency: "INR",
      });
      const { razorpay_order_id, key_id, amount } = razorpayOrder.data;

      sessionStorage.setItem("pendingOrderData", JSON.stringify(orderData));

      const options = {
        key: key_id,
        amount,
        currency: "INR",
        name: "RRIDE MOTO STUDIO",
        description: `${latestItemCount} item${latestItemCount === 1 ? "" : "s"} from cart`,
        order_id: razorpay_order_id,
        prefill: {
          name: activeUser?.name || "",
          email: activeUser?.email || "",
          contact: activeUser?.mobile || activeUser?.phone || "",
        },
        notes: {
          user_id: activeUser?.id,
          bike: selectedBike ? `${selectedBike.brand} ${selectedBike.model} ${selectedBike.variant}` : "not_selected",
        },
        theme: { color: "#ffcc32" },
        handler: async (response) => {
          try {
            const pendingOrderData = sessionStorage.getItem("pendingOrderData");

            if (!pendingOrderData) {
              throw new Error("Order details are missing - cannot verify payment");
            }

            if (!response.razorpay_order_id || !response.razorpay_payment_id || !response.razorpay_signature) {
              throw new Error("Missing required payment verification fields");
            }

            const verifyPayload = {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              order_data: JSON.parse(pendingOrderData),
            };

            await api.post("/razorpay/verify-payment", verifyPayload);

            sessionStorage.removeItem("pendingOrderData");

            toast.success("Payment successful!");
            await refreshUser?.();
            refreshCartCount();
            navigate("/orders");
          } catch (error) {
            console.error("Payment verification error:", error);

            let errorMessage = "Payment verification failed";
            if (error.response?.data) {
              if (typeof error.response.data === "string") {
                errorMessage = error.response.data;
              } else if (error.response.data.detail) {
                errorMessage = typeof error.response.data.detail === "string"
                  ? error.response.data.detail
                  : JSON.stringify(error.response.data.detail);
              } else if (error.response.data.message) {
                errorMessage = error.response.data.message;
              }
            } else if (error.message) {
              errorMessage = error.message;
            }

            toast.error(errorMessage);
          }
        },
        modal: {
          ondismiss: function () {
            sessionStorage.removeItem("pendingOrderData");
            toast.info("Payment cancelled");
          }
        },
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();
    } catch (err) {
      console.error(err);
      toast.error(getApiMessage(err, err?.message || "Payment failed"));
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center text-[#ffcc32] text-lg" style={{ fontFamily: "Inter, Poppins, Roboto, Arial, sans-serif" }}>
        Loading cart...
      </div>
    );
  }

  if (cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center text-white px-6 pt-16" style={{ fontFamily: "Inter, Poppins, Roboto, Arial, sans-serif" }}>
        <ShoppingCart size={48} className="text-[#ffcc32] mb-4" />
        <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.08em" }}>Your Cart Is Empty</h1>
        <button
          onClick={() => navigate("/products")}
          className="px-6 py-2.5 bg-[#ffcc32] rounded-xl font-bold text-black hover:bg-[#ffda5c] transition"
        >
          Start Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-16 pb-32 lg:pb-10 px-3 sm:px-4" style={{ fontFamily: "Inter, Poppins, Roboto, Arial, sans-serif" }}>
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 sm:mb-8"
        >
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-black uppercase mb-2" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.08em" }}>
            Your Cart{" "}
            <span className="text-[#ffcc32]">({itemCount})</span>
          </h1>
          <p className="text-zinc-400 text-sm">Review your items before checkout</p>
        </motion.div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_380px] gap-5 lg:gap-8 items-start">
          <div className="space-y-3 sm:space-y-4 min-w-0">
            <AnimatePresence>
              {cart.items.map((item) => (
                <motion.div
                  layout
                  key={cartLineKey(item)}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -30 }}
                  className="rounded-[20px] border border-[#ffcc32]/10 bg-[#090909] p-3 sm:p-4 shadow-[0_18px_45px_rgba(0,0,0,0.38)] transition-all hover:border-[#ffcc32]/30"
                >
                  <div className="grid grid-cols-[86px,minmax(0,1fr)] gap-3 sm:grid-cols-[112px,minmax(0,1fr)] sm:gap-4">
                    <button
                      type="button"
                      onClick={() => navigate(`/products/${item.product_id}`)}
                      className="h-[86px] w-[86px] sm:h-28 sm:w-28 rounded-2xl overflow-hidden shrink-0 bg-zinc-950 border border-white/10 focus:outline-none focus:ring-2 focus:ring-[#ffcc32]"
                    >
                      <img
                        src={getProductImage(item)}
                        alt={item.name}
                        loading="lazy"
                        decoding="async"
                        className="w-full h-full object-contain hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          console.log("Image failed to load, trying fallback");
                          // Fallback to first product image if available
                          if (item.images?.[0] && e.target.src !== item.images[0]) {
                            e.target.src = item.images[0];
                          } else if (item.product?.images?.[0] && e.target.src !== item.product.images[0]) {
                            e.target.src = item.product.images[0];
                          } else {
                            e.target.src = "/placeholder.svg";
                          }
                        }}
                      />
                    </button>

                    <div className="min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <h3
                            onClick={() => navigate(`/products/${item.product_id}`)}
                            className="cursor-pointer text-[15px] sm:text-lg font-semibold leading-snug text-white line-clamp-2 hover:text-[#ffcc32] transition-colors break-words"
                            style={{ fontFamily: "Inter, Poppins, Roboto, Arial, sans-serif" }}
                          >
                            {item.name}
                          </h3>
                          <p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-[#ffcc32]/80">
                            {item.item_type === "combo" ? "Combo Pack" : item.product_type || "Product"}
                          </p>
                        </div>

                        <button
                          type="button"
                          aria-label={`Remove ${item.name}`}
                          onClick={() => removeItem(item.product_id, item.size, item.color)}
                          className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-red-400/20 bg-red-500/10 text-red-300 transition hover:border-red-300/40 hover:text-red-200 disabled:opacity-50"
                          disabled={updating === item.product_id + (item.size || "") + (item.color || "")}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {item.item_type === "combo" && item.selected_children && item.selected_children.length > 0 ? (
                        <div className="mt-3 space-y-1.5">
                          {item.selected_children.map((child, idx) => (
                            <div key={idx} className="rounded-xl border border-white/10 bg-black/40 px-2.5 py-2 text-[12px] text-zinc-300">
                              <p className="font-medium text-white line-clamp-1">{child.name}</p>
                              <p className="mt-0.5 text-zinc-400">
                                Color: <span className="text-[#ffcc32]">{child.color || "Standard"}</span>
                                <span className="mx-1.5 text-zinc-600">|</span>
                                Size: <span className="text-[#ffcc32]">{child.size || "Standard"}</span>
                                <span className="mx-1.5 text-zinc-600">|</span>
                                Qty: {child.quantity}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="mt-3 flex flex-wrap gap-2 text-[12px]">
                          <span className="rounded-full border border-[#ffcc32]/20 bg-[#ffcc32]/10 px-2.5 py-1 text-zinc-200">
                            Color: <span className="font-semibold text-[#ffcc32]">{item.color || "Standard"}</span>
                          </span>
                          <span className="rounded-full border border-[#ffcc32]/20 bg-[#ffcc32]/10 px-2.5 py-1 text-zinc-200">
                            Size: <span className="font-semibold text-[#ffcc32]">{item.size || "Standard"}</span>
                          </span>
                        </div>
                      )}

                      <div className="mt-4 grid grid-cols-2 gap-2 border-t border-white/10 pt-3">
                        <div>
                          <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Product Price</p>
                          <p className="mt-0.5 text-sm sm:text-base font-bold text-[#ffcc32]">
                            {money(item.price)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">Total Price</p>
                          <p className="mt-0.5 text-sm sm:text-base font-bold text-white">
                            {money(Number(item.quantity || 0) * Number(item.price || 0))}
                          </p>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center justify-between gap-3">
                        <div className="inline-flex h-10 items-center rounded-full border border-[#ffcc32]/20 bg-black/70 p-1 shadow-inner shadow-black">
                          <button
                            type="button"
                            aria-label={`Decrease ${item.name} quantity`}
                            disabled={updating === item.product_id + (item.size || "") + (item.color || "")}
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.size, item.color)}
                            className="grid h-8 w-8 place-items-center rounded-full text-zinc-300 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="w-8 text-center text-sm font-bold text-white">{item.quantity}</span>
                          <button
                            type="button"
                            aria-label={`Increase ${item.name} quantity`}
                            disabled={updating === item.product_id + (item.size || "") + (item.color || "")}
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.size, item.color)}
                            className="grid h-8 w-8 place-items-center rounded-full text-zinc-300 transition hover:bg-white/10 hover:text-white disabled:opacity-50"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <span className="text-[11px] uppercase tracking-[0.14em] text-zinc-500">
                          Qty selector
                        </span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {recommendations.length > 0 && (
              <div className="mt-8">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.06em" }}>
                  <Sparkles className="text-[#ffcc32]" size={20} />
                  Complete Your Order
                </h2>
                <div className="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-hide pb-2 pr-[calc(16%+12px)] sm:pr-[calc(18%+16px)] snap-x snap-mandatory">
                  {recommendations.map((product) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-[#0a0a0a] border border-white/10 rounded-2xl overflow-hidden hover:border-[#ffcc32]/30 transition-all group shadow-[0_16px_36px_rgba(0,0,0,0.28)] flex-shrink-0 snap-start"
                      style={{ 
                        width: '240px', 
                        minWidth: '240px' 
                      }}
                      onClick={() => navigate(`/products/${product.id}`)}
                    >
                      <div className="flex">
                        {/* Image section */}
                        <div className="w-20 h-20 sm:w-24 sm:h-24 overflow-hidden flex-shrink-0">
                          <img
                            src={getProductImage(product)}
                            alt={product.name}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        
                        {/* Content section */}
                        <div className="p-2 sm:p-3 flex flex-col flex-1 justify-center">
                          <h3 className="font-semibold text-white line-clamp-2 mb-2" style={{ 
                            fontFamily: "Inter, Poppins, Roboto, Arial, sans-serif",
                            fontSize: '11px sm:13px'
                          }}>
                            {product.name}
                          </h3>
                          <p className="text-[#ffcc32] font-bold" style={{ fontSize: '12px sm:14px' }}>
                            {money(product.price)}
                          </p>
                          
                          {/* Direct add to cart button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              addRecommendation(product);
                            }}
                            className="mt-2 w-8 h-8 bg-[#ffcc32] hover:bg-[#ffda5c] text-black rounded-lg transition-colors flex items-center justify-center"
                          >
                            <ShoppingCart size={14} />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="min-w-0">
            <div className="sticky top-24 rounded-[22px] border border-[#ffcc32]/20 bg-[#080808] p-4 sm:p-5 shadow-[0_24px_70px_rgba(0,0,0,0.42)]">
              <h2 className="text-lg sm:text-xl font-bold uppercase" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.08em" }}>Order Summary</h2>

              <div className="mt-5 border-t border-white/10 pt-4">
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h3 className="text-sm font-semibold flex items-center gap-2">
                    <MapPin size={16} className="text-[#ffcc32]" />
                    Delivery Address
                  </h3>
                  <button
                    type="button"
                    onClick={() => openAddressModal({ adding: !selectedAddress && addresses.length === 0 })}
                    className="shrink-0 text-[#ffcc32] text-xs font-bold uppercase hover:text-[#ffda5c]"
                  >
                    {selectedAddress ? "Change" : "Add"}
                  </button>
                </div>
                {selectedAddress ? (
                  <p className="text-sm leading-5 text-zinc-200 break-words">{formatAddress(selectedAddress)}</p>
                ) : (
                  <p className="text-sm text-zinc-500">No delivery address selected.</p>
                )}
              </div>

              <div className="mt-5 space-y-2.5 border-t border-white/10 pt-4">
                <div className="flex justify-between gap-4 text-sm">
                  <span className="text-zinc-400">Subtotal</span>
                  <span className="text-white">{money(subtotal)}</span>
                </div>

                <div className="flex justify-between gap-4 text-sm">
                  <span className="text-zinc-400">Shipping</span>
                  <span className={`text-right break-words ${shippingError ? "max-w-[58%] text-red-300" : "text-white"}`}>
                    {shippingLoading
                      ? "Calculating..."
                      : shippingError
                        ? shippingError
                        : shippingInfo?.serviceable
                          ? shippingCharge === 0 ? "Free" : money(shippingCharge)
                          : selectedAddress ? "Unable to calculate" : "Add address"}
                  </span>
                </div>

                <div className="flex justify-between gap-4 text-sm">
                  <span className="text-zinc-400">Discount</span>
                  <span className="text-white">{discount > 0 ? `- ${money(discount)}` : money(0)}</span>
                </div>

                <div className="border-t border-white/10 pt-3 flex justify-between gap-4 text-lg font-bold">
                  <span className="text-white">Grand Total</span>
                  <span className="text-[#ffcc32]">{money(total)}</span>
                </div>
              </div>

              <div className="mt-5 space-y-2 border-t border-white/10 pt-4 text-xs">
                <div className="flex justify-between gap-4">
                  <span className="text-zinc-500">Courier</span>
                  <span className="text-right text-zinc-200 break-words">{shippingInfo?.courier_name || "After shipping check"}</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-zinc-500">Estimated Delivery</span>
                  <span className="text-right text-zinc-200">
                    {shippingInfo?.estimated_delivery_days ? `${shippingInfo.estimated_delivery_days} Days` : "After shipping check"}
                  </span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-zinc-500">COD Availability</span>
                  <span className="text-right text-zinc-200">{codAvailability}</span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleDirectCheckout}
                disabled={checkoutDisabled}
                className="hidden lg:flex w-full mt-6 py-4 bg-[#ffcc32] hover:bg-[#ffda5c] disabled:opacity-60 disabled:cursor-not-allowed rounded-2xl font-black text-black transition items-center justify-center gap-2 text-base uppercase shadow-[0_0_28px_rgba(255,204,49,0.25)]"
              >
                {processing ? <Loader size={20} className="animate-spin" /> : <CreditCard size={20} />}
                Pay Securely
              </button>

              <div className="mt-5 grid grid-cols-2 gap-2 border-t border-white/10 pt-4 text-[11px] text-zinc-400">
                <div className="flex items-center gap-2 min-w-0">
                  <ShieldCheck size={15} className="shrink-0 text-[#ffcc32]" />
                  <span className="truncate">Secure Payments</span>
                </div>
                <div className="flex items-center gap-2 min-w-0">
                  <Truck size={15} className="shrink-0 text-[#ffcc32]" />
                  <span className="truncate">Eligible Free Delivery</span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <AnimatePresence>
        {addressModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/75 p-3 sm:p-6 backdrop-blur-sm"
            onClick={() => setAddressModalOpen(false)}
          >
            <motion.div
              initial={{ y: 24, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 24, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="w-full max-w-3xl max-h-[90vh] flex flex-col rounded-[22px] border border-[#ffcc32]/20 bg-[#080808] shadow-[0_30px_90px_rgba(0,0,0,0.6)]"
              onClick={(event) => event.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-start justify-between gap-3 p-4 sm:p-6 border-b border-white/10">
                <div>
                  <h2 className="text-xl font-bold uppercase text-white" style={{ fontFamily: "Cinzel, serif", letterSpacing: "0.08em" }}>
                    Delivery Address
                  </h2>
                  <p className="mt-1 text-sm text-zinc-500">Saved Addresses</p>
                </div>
                <button
                  type="button"
                  aria-label="Close address modal"
                  onClick={() => {
                    setAddressModalOpen(false);
                    setShowAddressForm(false);
                    setEditingAddressId(null);
                  }}
                  className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/10 text-zinc-300 hover:border-[#ffcc32]/40 hover:text-white"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <p className="text-sm text-zinc-400 mb-4">
                  {addresses.length ? `${addresses.length} saved address${addresses.length === 1 ? "" : "es"}` : "No saved addresses yet"}
                </p>

                {addresses.length > 0 && (
                  <div className="grid gap-3">
                    {addresses.map((address) => {
                      const isSelected = selectedAddress?.id === address.id;
                      return (
                        <div
                          key={address.id}
                          onClick={() => selectAddress(address)}
                          className={`rounded-2xl border p-3 sm:p-4 transition cursor-pointer hover:border-[#ffcc32]/50 ${
                            isSelected
                              ? "border-[#ffcc32] bg-[#ffcc32]/10"
                              : "border-white/10 bg-black/40"
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5">
                              {isSelected ? (
                                <CheckCircle size={18} className="text-[#ffcc32]" />
                              ) : (
                                <MapPin size={18} className="text-zinc-500" />
                              )}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm leading-5 text-white break-words">{formatAddress(address)}</p>
                              <p className="mt-1 text-xs text-zinc-500">Pincode: {address.pincode || "Not set"}</p>
                            </div>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-2 pl-0 sm:pl-8">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                startEditAddress(address);
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-white/10 px-3 py-2 text-xs font-bold uppercase text-zinc-200 hover:border-[#ffcc32]/40"
                            >
                              <Edit3 size={13} />
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAddress(address.id);
                              }}
                              className="inline-flex items-center gap-1 rounded-full border border-red-400/20 px-3 py-2 text-xs font-bold uppercase text-red-300 hover:border-red-300/40"
                            >
                              <Trash2 size={13} />
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Sticky Bottom Button */}
              <div className="p-4 sm:p-6 border-t border-white/10 bg-[#080808]">
                <button
                  onClick={openAddAddressModal}
                  className="w-full py-3 bg-[#ffcc32] hover:bg-[#ffda5c] text-black rounded-xl font-bold text-sm transition"
                >
                  Add New Address
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ y: 80 }}
        animate={{ y: 0 }}
        className="fixed bottom-0 left-0 right-0 lg:hidden bg-[#060606]/95 backdrop-blur-xl border-t border-[#ffcc32]/20 z-50 shadow-[0_-18px_45px_rgba(0,0,0,0.45)]"
      >
        <div className="p-3 sm:p-4 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <button
              onClick={() => setMobileSummaryOpen(!mobileSummaryOpen)}
              className="flex items-center gap-1 text-zinc-400 text-xs mb-0.5"
            >
              Subtotal
              {mobileSummaryOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>
            <span className="text-[#ffcc32] font-bold text-lg">
              {money(subtotal)}
            </span>
          </div>
          <button
            onClick={handleDirectCheckout}
            disabled={checkoutDisabled}
            className="shrink-0 px-3.5 sm:px-5 py-3 bg-[#ffcc32] rounded-xl font-black text-black flex items-center gap-1.5 text-[12px] sm:text-sm uppercase disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
          >
            {processing ? "Processing..." : "Proceed to Checkout"} <ArrowRight size={15} />
          </button>
        </div>

        <AnimatePresence>
          {mobileSummaryOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden px-4 pb-4 space-y-2 text-sm border-t border-white/10"
            >
              <div className="flex justify-between text-zinc-400">
                <span>Subtotal</span>
                <span>{money(subtotal)}</span>
              </div>
              {shippingLoading ? (
                <div className="flex justify-between text-zinc-400">
                  <span>Shipping</span>
                  <span className="text-zinc-500">Calculating...</span>
                </div>
              ) : shippingError ? (
                <div className="flex justify-between gap-3 text-red-400">
                  <span>Shipping</span>
                  <span className="text-right break-words">{shippingError}</span>
                </div>
              ) : shippingInfo?.serviceable ? (
                <>
                  <div className="flex justify-between text-zinc-400">
                    <span>Shipping</span>
                    <span>{shippingCharge === 0 ? "Free" : money(shippingCharge)}</span>
                  </div>
                  {shippingInfo.courier_name && (
                    <div className="flex justify-between gap-3 text-zinc-400">
                      <span>Courier</span>
                      <span className="text-right text-[#ffcc32] break-words">{shippingInfo.courier_name}</span>
                    </div>
                  )}
                  {shippingInfo.estimated_delivery_days && (
                    <div className="flex justify-between text-zinc-400">
                      <span>Delivery</span>
                      <span>{shippingInfo.estimated_delivery_days} Days</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex justify-between text-zinc-400">
                  <span>Shipping</span>
                  <span className="text-right text-red-400">Unable to calculate</span>
                </div>
              )}
              <div className="flex justify-between text-zinc-400">
                <span>Discount</span>
                <span>{discount > 0 ? `- ${money(discount)}` : money(0)}</span>
              </div>
              <div className="flex justify-between text-zinc-400">
                <span>COD</span>
                <span>{codAvailability}</span>
              </div>
              <div className="border-t border-white/10 pt-2 flex justify-between font-bold">
                <span>Grand Total</span>
                <span className="text-[#ffcc32]">{money(total)}</span>
              </div>
              <div className="border-t border-white/10 pt-2">
                <p className="text-zinc-400 text-xs mb-1">Delivery address</p>
                {selectedAddress ? (
                  <div>
                    <p className="text-white text-xs break-words">{formatAddress(selectedAddress)}</p>
                    <button
                      onClick={() => {
                        setMobileSummaryOpen(false);
                        openAddressModal();
                      }}
                      className="text-[#ffcc32] text-xs font-bold uppercase mt-1"
                    >
                      Change Address
                    </button>
                  </div>
                ) : (
                  <button 
                    onClick={() => {
                      setMobileSummaryOpen(false);
                      openAddressModal({ adding: true });
                    }} 
                    className="text-[#ffcc32] text-xs font-bold uppercase"
                  >
                    Add Address
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Address Edit Modal */}
      <AnimatePresence>
        {showAddressEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[80] flex items-center justify-center p-4"
            onClick={closeAddressEditModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-black border border-white/10 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Edit3 size={20} className="text-[#ffcc32]" />
                  {editingAddressId ? 'Edit Address' : 'Add New Address'}
                </h2>
                <button
                  onClick={closeAddressEditModal}
                  className="text-zinc-400 hover:text-white transition"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Domestic/International Toggle */}
              <div className="mb-4 flex items-center gap-3">
                <span className="text-sm text-zinc-400">Shipping Type:</span>
                <div className="flex items-center gap-2 bg-black border border-white/10 rounded-lg p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsInternational(false);
                      setAddressForm(prev => ({ ...prev, country: 'India' }));
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                      !isInternational 
                        ? 'bg-emerald-600 text-white' 
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    Domestic (India)
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsInternational(true);
                      setAddressForm(prev => ({ ...prev, country: '' }));
                    }}
                    className={`px-3 py-1.5 rounded-md text-xs font-bold transition ${
                      isInternational 
                        ? 'bg-orange-600 text-white' 
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    International
                  </button>
                </div>
              </div>

              {/* Location Autofill */}
              <div className="mb-4 flex items-center gap-3">
                <LocationAutofillButton onAutofill={applyLocationAutofill} />
                <p className="text-xs text-zinc-500">
                  Auto-fill from current location
                </p>
              </div>

              {/* Compact Grid Layout */}
              <div className="grid grid-cols-2 gap-3">
                {/* Required Fields */}
                <input
                  type="text"
                  placeholder="House / Flat *"
                  value={addressForm.house_flat_no || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, house_flat_no: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                <input
                  type="text"
                  placeholder="Street / Area *"
                  value={addressForm.street_area || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, street_area: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                
                {/* Optional Fields */}
                <input
                  type="text"
                  placeholder="Building (optional)"
                  value={addressForm.building_name || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, building_name: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                <input
                  type="text"
                  placeholder="Apt/Suite (optional)"
                  value={addressForm.apartment_suite || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, apartment_suite: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                <input
                  type="text"
                  placeholder="Floor (optional)"
                  value={addressForm.floor || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, floor: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                <input
                  type="text"
                  placeholder="Landmark (optional)"
                  value={addressForm.landmark || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, landmark: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                
                {/* Location Fields */}
                <input
                  type="text"
                  placeholder="City *"
                  value={addressForm.city || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                {!isInternational ? (
                  <div className="relative">
                    <select
                      value={addressForm.state_province}
                      onChange={(e) => setAddressForm({ ...addressForm, state_province: e.target.value })}
                      className="w-full rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32] appearance-none cursor-pointer"
                    >
                      <option value="">Select State *</option>
                      {INDIAN_STATES.map((state) => (
                        <option key={state} value={state}>
                          {state}
                        </option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
                  </div>
                ) : (
                  <input
                    type="text"
                    placeholder="State/Province *"
                    value={addressForm.state_province || ""}
                    onChange={(e) => setAddressForm({ ...addressForm, state_province: e.target.value })}
                    className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                  />
                )}
                
                {/* Country & Postal Code */}
                <input
                  type="text"
                  placeholder="Country *"
                  value={addressForm.country || ""}
                  onChange={(e) => {
                    setAddressForm({ ...addressForm, country: e.target.value });
                    if (e.target.value && e.target.value.toLowerCase() !== 'india') {
                      setIsInternational(true);
                    } else if (e.target.value && e.target.value.toLowerCase() === 'india') {
                      setIsInternational(false);
                    }
                  }}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                  readOnly={!isInternational}
                />
                <input
                  type="text"
                  inputMode={isInternational ? "text" : "numeric"}
                  placeholder={isInternational ? 'Postal Code *' : 'Pincode *'}
                  value={addressForm.postal_code || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
                  maxLength={isInternational ? 10 : 6}
                  className="rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
                
                {/* District/County */}
                <input
                  type="text"
                  placeholder={isInternational ? 'County (optional)' : 'District (optional)'}
                  value={addressForm.district_county || ""}
                  onChange={(e) => setAddressForm({ ...addressForm, district_county: e.target.value })}
                  className="col-span-2 rounded-xl border border-white/10 bg-black px-3 py-2 text-sm text-white placeholder-zinc-600 outline-none transition focus:border-[#ffcc32]"
                />
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={closeAddressEditModal}
                  className="flex-1 px-4 py-2 border border-white/20 rounded-xl text-zinc-300 hover:text-white hover:border-white/40 transition"
                >
                  Cancel
                </button>
                <button
                  onClick={saveAddress}
                  className="flex-1 px-4 py-2 bg-[#ffcc32] hover:bg-[#ffda5c] text-black rounded-xl font-bold transition"
                >
                  {editingAddressId ? 'Update' : 'Add Address'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
